import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface LocationState {
  overallRating?: number;
  contractorName?: string;
  reviewText?: string | null;
}

function CheckmarkAnimation() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  const circumference = 2 * Math.PI * 36;

  return (
    <div style={{
      width: 96, height: 96, borderRadius: '50%',
      background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px',
    }}>
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle
          cx="48" cy="48" r="36"
          fill="none" stroke="#22c55e" strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={visible ? 0 : circumference}
          strokeLinecap="round"
          transform="rotate(-90 48 48)"
          style={{ transition: 'stroke-dashoffset 0.55s ease' }}
        />
        <polyline
          points="30,50 42,62 66,36"
          fill="none" stroke="#22c55e" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="50"
          strokeDashoffset={visible ? 0 : 50}
          style={{ transition: 'stroke-dashoffset 0.4s ease 0.4s' }}
        />
      </svg>
    </div>
  );
}

function StarRow({ rating }: { rating: number }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: 20, letterSpacing: 2 }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  );
}

export function ReviewConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const rating = state.overallRating ?? 5;

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0, textAlign: 'center' }}>
          Review Submitted
        </h1>
      </div>

      <div style={{ flex: 1, padding: '40px 20px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        <CheckmarkAnimation />

        <div style={{ textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: '#111827' }}>
            Your review is now live
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280', maxWidth: 280 }}>
            Thank you for helping the Home Guard community find great contractors.
          </p>
        </div>

        {/* Health score badge */}
        <div style={{
          background: 'linear-gradient(135deg, #1D4ED8 0%, #1e40af 100%)',
          borderRadius: 16, padding: '16px 24px',
          display: 'flex', alignItems: 'center', gap: 12, width: '100%', maxWidth: 320,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>
            🏠
          </div>
          <div>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 500 }}>
              Home Health Score
            </p>
            <p style={{ margin: '2px 0 0', color: '#fff', fontSize: 18, fontWeight: 700 }}>
              +3 points
            </p>
          </div>
        </div>

        {/* Review preview card */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 20,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: '100%', maxWidth: 400,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#111827' }}>
                {state.contractorName ?? 'Your contractor'}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>
                {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <StarRow rating={rating} />
          </div>
          {state.reviewText && (
            <p style={{
              margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6,
              display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {state.reviewText}
            </p>
          )}
          {!state.reviewText && (
            <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
              No written review provided.
            </p>
          )}
        </div>
      </div>

      {/* Bottom action */}
      <div style={{ padding: '12px 20px 32px' }}>
        <button
          type="button"
          onClick={() => navigate('/app/dashboard', { replace: true })}
          style={{
            width: '100%', padding: 14,
            background: '#1D4ED8', color: '#fff',
            border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
